import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { cloneDeep } from 'lodash';

import { TargetingSearch } from '../models/targeting-search.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { TargetingList } from '../models/targeting-list.model';
import { TargetingListsApiService } from '../../../api/targeting/targeting-lists.api.service';
import { DiscoverRoutingService } from '../../DiscoverRouter/services/discover-routing.service';
import { FilterTypeModel } from '../../../Common/Filter/models/abstract-filter-type.model';
import { JournodexSubjectsService } from '../../../Common/Journodex/services/journodex-subjects.service';
import { JournodexEnumsService } from '../../Core/services/journodex-enums.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { TargetingQueryApi, TargetingSearchApi } from '../../../api/targeting/model/targeting-search.api.model';
import { OutletsPublicApi } from '../../../api/targeting/outlets-public.api';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { BaseSearchService, FacetValueModel, TargetingFacetCollection } from '../services/base-search.service';
import { TargetingListList } from '../models/targeting-list-list.model';
import { TargetingSearchList } from '../models/targeting-search-list.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { TargetingResultFactory } from '../factories/targeting-result.factory';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { DistributionApi } from '../../../api/distributions/distribution.api';
import { RecipientSearchResultsApiModel } from '@api/distributions/model/recipient-search-results.api.model';
import { TargetingSearchFacetsEnum } from '@api/targeting/enum/targeting-search-facets.enum';
import { SortFieldEnum } from '@Common/TargetingShared/enum/SortField.enum';
import { DistributionReportStat } from '@Common/TargetingShared/model/statisticsReport/distribution-report-stat-api.model';
import { DistributionSummaryModel } from '@Common/TargetingShared/model/distribution-summary-model';
import { RecipientSearchModel } from '../components/Distribution/DistributionReport/RecipientSearch/recipient-search.model';
import { DistributionStatus } from '@Common/TargetingShared/enum/distribution-status.enum';
import { InteractionsExportApi } from '../../../api/targeting/model/interactions-export.api';
import { CsvInteractionExportType } from '../components/TargetingResults/CsvInteractionExportDialog/csv-interaction-export-type.enum';
import { TargetingMiscApi } from '../../../api/targeting/targeting-misc.api';
import { EntityCountApi } from '../../../api/targeting/model/entity-count.api';
import { TargetingSearchesService } from './targeting-searches.service';

export enum SourceType {
    lists,
    searches,
    allPrivateContacts,
    distributions,
    portfolios,
    allInteractions
}

export interface SourcePickerFacetSearchOptions {
    isMain?: boolean;
    isFiltered?: boolean;
}

@Injectable()
export class SourcePickerService extends BaseSearchService {

    public search: TargetingSearch;
    private list: TargetingListList;
    private distribution: DistributionSummaryModel;
    private allInteractions: InteractionsExportApi;


    private entityCountSubject = new BehaviorSubject<EntityCountApi>(null);
    public entityCount$ = this.entityCountSubject.asObservable();

    constructor(
        protected discoverRoutingService: DiscoverRoutingService,
        protected targetingSearchesApiService: TargetingSearchesApiService,
        protected targetingSearchesService: TargetingSearchesService,
        protected targetingListsApiService: TargetingListsApiService,
        protected subjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected journodexRegionService: JournodexRegionsService,
        protected localizationService: LocalizationService,
        protected contactsPublicApi: ContactsPublicApi,
        protected outletsPublicApi: OutletsPublicApi,
        protected targetingResultFactory: TargetingResultFactory,
        protected currentUserStateService: CurrentUserStateService,
        protected distributionApi: DistributionApi,
        protected targetingMiscApi: TargetingMiscApi
    ) {
        super(
            targetingSearchesApiService,
            targetingListsApiService,
            subjectsService,
            journodexEnumsService,
            journodexRegionService,
            localizationService,
            targetingResultFactory,
            currentUserStateService,
            distributionApi
        );

        this.setFilters(this.allFilters);
    }

    getFilterDefinitions(includeStreamContentFilter: boolean = true): FilterTypeModel[] {
        let filters: FilterTypeModel[] = [
            this.audienceReachFilter,
            this.coverageFocusFilter,
            this.subjectFilter,
            this.productFrequencyFilter,
            this.jobRoleFilter,
            this.jobTitleFilter,
            this.languageFilter,
            this.listFilter,
            this.locationFilter,
            this.marketSectorFilter,
            this.mediaTypeFilter,
            this.contactNameFilter,
            this.outletNameFilter,
            this.productTypeFilter,
        ];
        if (includeStreamContentFilter) {
            filters.push(this.streamContentFilter);
        }
        // TODO : rather than excluding it, we probably need to have it
        // but with outlet labels.
        if ((this.search && this.search.searchMode !== SearchModeEnum.outlet)
            || this.list || this.distribution) {
            filters.push(this.statusFilter);
        } else {
            filters = filters.filter(x => x !== this.jobTitleFilter);
        }
        if (this.list) {
            filters = filters.filter(x => x !== this.outletNameFilter);
        }

        filters.push(
            this.targetAudienceFilter,
            this.targetAudienceRegionFilter
        );
        // TODO: add aoc and list filter, alphabetically. Backend not implemented at the moment

        return filters;
    }

    setActiveSearch(searchSummary: TargetingSearchList, includeStreamContentFilter: boolean = true) {
        this.list = null;
        this.distribution = null;
        this.targetingSearchesService.getById(searchSummary.id).subscribe(search => {
            this.search = search;
            this.doSearch();
            this.setFilters(this.getFilterDefinitions(includeStreamContentFilter));
        });
    }

    setActiveList(list: TargetingListList, includeStreamContentFilter: boolean = true) {
        this.search = null;
        this.distribution = null;
        this.list = list;
        this.setFilters(this.getFilterDefinitions(includeStreamContentFilter));
        this.doSearch();
    }

    setActiveDistribution(distribution: DistributionSummaryModel, includeStreamContentFilter: boolean = true) {
        this.search = null ;
        this.list = null;
        this.distribution = distribution;

        this.setFilters(this.getFilterDefinitions(includeStreamContentFilter));
        this.doSearch();

    }

    setAllPrivateContacts() {
        const privateContactSearch = new TargetingSearch();
        privateContactSearch.privateContactsOnly = true;
        this.search = privateContactSearch;
        this.doSearch();
    }

    setAllInteractions() {
        this.search = null ;
        this.list = null;
        this.distribution = null;

        const allInteractionSearch = new InteractionsExportApi();
        allInteractionSearch.isAllInteractions = true;
        allInteractionSearch.types = [
            CsvInteractionExportType.DISTRIBUTION,
            CsvInteractionExportType.DIRECT_MESSAGE,
            CsvInteractionExportType.EMAIL,
            CsvInteractionExportType.OTHER,
            CsvInteractionExportType.PHONE
        ];
        allInteractionSearch.dateRange = {
            createdFrom: null,
            createdTo: null
        };
        this.allInteractions = allInteractionSearch;

        this.doSearch();
    }

    public doSearch() {

        this.nResults = null;
        this.nResults$.next(this.nResults);

        if (this.mainSearchRequest) {
            this.mainSearchRequest.unsubscribe();
            this.mainSearchRequest = null;
        }

        const search = cloneDeep(this.search || this.list || this.distribution || this.allInteractions);

        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }

        const mainSearchRequest: Observable<TargetingFacetCollection> = this.facetSearch(search, null, {isMain: true, isFiltered});

        if (!mainSearchRequest) {
            return;
        }
        // just to make sure it runs, in case the caller doesn't subscribe
        this.searchLoading$.next(true);
        this.mainSearchRequest = mainSearchRequest.pipe(mergeMap(
            (collection) => {
                this.setPrimaryFacets(collection);
                if (isFiltered) {
                    return this.facetSearch(search, filters);
                }
                return of(null);
            }
        )).subscribe({
            next: () => {
                this.mainSearchRequest = null;
                this.searchLoading$.next(false);
            },
            error: (err) => {
                console.log(err);
                this.searchLoading$.next(false);
            }
        });
    }

    // Override to extract result count
    facetSearch(
        search: TargetingSearch | TargetingList | RecipientSearchModel | InteractionsExportApi,
        filters?: TargetingSearch,
        options?: SourcePickerFacetSearchOptions
    ): Observable<TargetingFacetCollection> {
        let searchRequest: Observable<any>;

        if (search instanceof TargetingSearch) {
            searchRequest = this.targetingSearchesApiService.performSearch(
                search.toApiModel(true, filters).query,
                0,
                0
            );
        } else if (search instanceof TargetingListList) {
            const listQuery = new TargetingSearch();
            listQuery.list.include = [search.id];
            searchRequest = this.targetingSearchesApiService.performSearch(
                listQuery.toApiModel(true, filters).query,
                0,
                0
            );
        } else if (search instanceof DistributionSummaryModel) {
            const recipientSearch = new RecipientSearchModel();
            recipientSearch.query = new TargetingSearch().toApiModel(true, filters).query;
            if (search.status === DistributionStatus.sent || search.status === DistributionStatus.scheduled) {
                recipientSearch.statType = DistributionReportStat.SENT;
                searchRequest = this.distributionApi.performRecipientSearch(
                    search.id,
                    recipientSearch,
                    0,
                    0,
                    this.sortOrder,
                    this.sortField ? this.sortField.enumValue : SortFieldEnum.CIRCULATION_UNIQUE_VISITORS
                );
            } else if ( search.status === DistributionStatus.draft) {
                searchRequest = this.distributionApi.performDraftDistributionRecipientSearch(search.id, recipientSearch.query);
            }
        } else if (search instanceof InteractionsExportApi) {
            search.query = new TargetingSearch().toApiModel(true, filters).query;
            searchRequest = this.targetingMiscApi.getEntityCountForInteractionExport(search);
        }

        return searchRequest?.pipe(
            tap(response =>{
                if (options?.isMain && options?.isFiltered) {
                    return;
                }
                this.nResults = (search instanceof InteractionsExportApi) ? response.contactCount + response.outletCount : response.numResults;
                this.nResults$.next(this.nResults);
            }),
            map(response => {
                if (search instanceof DistributionSummaryModel) {
                    return this.parseRecipientFacets(response);
                } else if (search instanceof InteractionsExportApi) {
                    this.entityCountSubject.next(response);
                    return this.parseRecipientFacets({
                        numResults: this.nResults,
                        facetResults: response.facetResults
                    });
                } else {
                    return this.parseFacets(response.facetResults);
                }
            })
        );
    }

    getCurrentSearchContext(): TargetingQueryApi {
        let filters: TargetingSearch;
        const isFiltered: boolean = this.hasFiltersToApply();

        if (isFiltered) {
            filters = this.generateFiltersSearch();
        }
        if (this.search) {
            return this.search.toApiModel(false, filters).query;
        } else if (this.distribution) {
            const distributionSearch = new TargetingSearch().toApiModel(false, filters);
            distributionSearch.query.searchMode = null;

            let filterQuery: TargetingQueryApi = new TargetingSearch().toApiModel().query;
            if (distributionSearch.query.filterQuery) {
                filterQuery = distributionSearch.query.filterQuery;
            }
            filterQuery.distribution.include = [this.distribution.id];

            distributionSearch.query.filterQuery = filterQuery;
            return distributionSearch.query;
        } else {
            const search = new TargetingSearch();
            const listSearch: TargetingSearchApi = search.toApiModel(false, filters);

            listSearch.query.searchMode = null;
            if (this.list?.id) {
                listSearch.query.list.include = [this.list.id];
            }

            return listSearch.query;
        }
    }

    getCurrentFilterSearchContext(): TargetingSearchApi {
        if (!this.hasFiltersToApply()) {
            return null;
        }
        return this.generateFiltersSearch().toApiModel();
    }

    private parseRecipientFacets(results: RecipientSearchResultsApiModel): TargetingFacetCollection {
        const facetCollection: TargetingFacetCollection = {
            facets: new Map<TargetingSearchFacetsEnum, FacetValueModel[]>(),
        };

        results.facetResults.forEach(facet => {
            facetCollection.facets.set(facet.facet, facet.counts);
        });
        return facetCollection;
    }

    setPrimaryFacets(collection: TargetingFacetCollection): void {
        if (collection) {
            this.primaryFacets = {
                facets: new Map(collection.facets),
            };
        } else {
            this.primaryFacets = null;
        }
        this.primaryFacets$.next(this.primaryFacets);
        this.setFilterOptions();
    }

    setFilterOptions(): void {
        if (!this.primaryFacets?.facets || this.primaryFacets?.facets.size === 0) {
            this.coverageFocusFilter.options = [];
            this.subjectFilter.options = [];
            this.productFrequencyFilter.options = [];
            this.jobRoleFilter.options = [];
            this.languageFilter.options = [];
            this.listFilter.options = [];
            this.marketSectorFilter.options = [];
            this.mediaTypeFilter.options = [];
            this.productTypeFilter.options = [];
            this.targetAudienceFilter.options = [];
            this.aocCountryFilter.options = [];
            this.aocProvinceFilter.options = [];
            this.aocCityFilter.options = [];
            this.countryFilter.options = [];
            this.provinceFilter.options = [];
            this.cityFilter.options = [];
        }
        super.setFilterOptions();
    }
}
